import React, { useEffect } from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Center,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { RTKQError } from '../../../../common/api/api-helpers';
import { CustomDatePicker } from '../../../../common/components/date-picker/CustomDatePicker';
import { SelectRetailer } from '../../../../common/components/SelectRetailer';
import { DEFAULT_DATE_FORMAT } from '../../../../common/constants';
import { useViewportType } from '../../../../common/hooks/use-viewport-type';
import { selectSite } from '../../../site/siteSlice';
import { ManualBillUploadFormData, useUploadManualBillMutation } from '../../billsApi';
import { manualBillUploadSchema } from './upload-bill-helpers';

// Make state nullable for the form
type NullableManualBillUploadFormData = {
  [K in keyof ManualBillUploadFormData]: ManualBillUploadFormData[K] | null;
};

const INITIAL_MANUAL_BILL_UPLOAD_FORM_DATA: NullableManualBillUploadFormData = {
  retailerId: null,
  startDate: null,
  endDate: null,
  totalAmount: null,
  billS3Path: '',
};

type ManualBillUploadProps = {
  billS3Path: string;
  onFinishUpload: () => void;
};

export const ManualBillUpload = ({ billS3Path, onFinishUpload }: ManualBillUploadProps) => {
  const {
    register,
    handleSubmit: handleFormSubmit,
    setValue,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(manualBillUploadSchema),
    defaultValues: INITIAL_MANUAL_BILL_UPLOAD_FORM_DATA,
  });
  const { site_id: siteId } = useSelector(selectSite);
  const { isDesktopViewport } = useViewportType();
  const [uploadManualBill, { isLoading: isUploading }] = useUploadManualBillMutation();
  const toast = useToast({
    duration: 3000,
    isClosable: true,
  });

  useEffect(() => {
    if (billS3Path) {
      setValue('billS3Path', billS3Path);
    }
  }, [billS3Path, setValue]);

  async function handleSubmit(values: NullableManualBillUploadFormData) {
    const formData = { ...values } as ManualBillUploadFormData;
    toast.closeAll();
    try {
      await uploadManualBill({
        formData,
        siteId,
      }).unwrap();
      toast({
        title: 'Your bill has been uploaded successfully!',
        status: 'success',
      });
      onFinishUpload();
    } catch (e) {
      const error = e as RTKQError;
      // This is custom error from backend
      let errorMessage = error?.originalError?.response?.data?.errors?.error?.[0]?.message;
      if (error.status === 409) errorMessage = 'Bill already exists for this period.';
      toast({
        title: errorMessage ?? 'Error uploading bill!',
        status: 'error',
      });
    }
  }

  return (
    <Box
      onSubmit={handleFormSubmit(handleSubmit)}
      as={'form'}
      data-testid="upload-manual-bill-form"
      w="100%"
      px={isDesktopViewport ? 0 : 4}
    >
      <Alert status="info" mb={6}>
        <AlertIcon />
        We could not parse your bill. Please enter the details manually.
      </Alert>
      <VStack spacing={4}>
        <FormControl isInvalid={!!errors.startDate}>
          <FormLabel>Start Date</FormLabel>
          <Controller
            control={control}
            name={`startDate`}
            render={({ field: { onChange, value } }) => (
              <CustomDatePicker
                withPortal
                inputProps={{
                  'data-testid': 'start-date',
                  placeholder: 'Bill Start Date',
                }}
                dateFormat={DEFAULT_DATE_FORMAT}
                onChange={onChange}
                selected={value}
              />
            )}
          />
          <FormErrorMessage data-testid="start-date-error-message">{errors.startDate?.message}</FormErrorMessage>
        </FormControl>
        <FormControl isInvalid={!!errors.endDate}>
          <FormLabel>End Date</FormLabel>
          <Controller
            control={control}
            name={`endDate`}
            render={({ field: { onChange, value } }) => (
              <CustomDatePicker
                withPortal
                inputProps={{
                  'data-testid': 'end-date',
                  placeholder: 'Bill End Date',
                }}
                dateFormat={DEFAULT_DATE_FORMAT}
                onChange={onChange}
                selected={value}
              />
            )}
          />
          <FormErrorMessage data-testid="end-date-error-message">{errors.endDate?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!errors.retailerId} pr={2} data-testid="retailer-id-form-control">
          <FormLabel>Retailer</FormLabel>
          <Controller
            control={control}
            name="retailerId"
            render={({ field: { onChange, onBlur, value, ref } }) => (
              <SelectRetailer
                ref={ref}
                onChange={(e) => {
                  onChange(e?.value);
                }}
                onBlur={onBlur}
                value={value}
              />
            )}
          />
          <FormErrorMessage data-testid="retailer-id-error-message">{errors.retailerId?.message}</FormErrorMessage>
        </FormControl>
        <FormControl isInvalid={!!errors.totalAmount}>
          <FormLabel>Total Bill Amount</FormLabel>
          <Input {...register('totalAmount')} placeholder="Total Bill Amount" data-testid="total-amount" />
          <FormErrorMessage data-testid="total-amount-error-message">{errors.totalAmount?.message}</FormErrorMessage>
          <FormHelperText>
            Note: The total bill amount should exclude any government bill relief, concessions, bonuses, or rebates. It
            should represent the total usage (including GST) minus any feed-in tariff credits.
          </FormHelperText>
        </FormControl>
      </VStack>

      <Center mt={8}>
        <Button
          data-testid="upload-bill-button"
          isLoading={isUploading}
          loadingText={'Uploading...'}
          colorScheme="white"
          rounded={20}
          w="100%"
          maxW={350}
          variant="outline"
          type="submit"
        >
          Upload Bill
        </Button>
      </Center>
    </Box>
  );
};
